import { Epic } from '../store/slices/epicsSlice';
import { Story } from '../store/slices/storiesSlice';
import { Category } from '../store/slices/categoriesSlice';

export interface EpicExportData {
  version: string;
  epics: Epic[];
  categories: Category[];
}

const VALID_STORY_TYPES = ['Engineering'];
const VALID_ENGINEERING_SUBTYPES = ['Frontend', 'Backend', 'Quality', 'DevOps', 'Fullstack'];

const isValidStory = (story: unknown): story is Story => {
  if (typeof story !== 'object' || story === null) return false;
  const s = story as Record<string, unknown>;
  if (typeof s.id !== 'string') return false;
  if (typeof s.title !== 'string') return false;
  if (typeof s.description !== 'string') return false;
  if (!VALID_STORY_TYPES.includes(s.type as string)) return false;
  if (typeof s.points !== 'number') return false;
  if (s.engineeringSubtype !== undefined && !VALID_ENGINEERING_SUBTYPES.includes(s.engineeringSubtype as string)) return false;
  return true;
};

const isValidEpic = (epic: unknown): epic is Epic => {
  if (typeof epic !== 'object' || epic === null) return false;
  const e = epic as Record<string, unknown>;
  if (typeof e.id !== 'string') return false;
  if (typeof e.title !== 'string') return false;
  if (typeof e.description !== 'string') return false;
  if (e.categoryId !== null && typeof e.categoryId !== 'string') return false;
  if (e.podId !== undefined && e.podId !== null && typeof e.podId !== 'string') return false;
  if (e.workstreamId !== undefined && e.workstreamId !== null && typeof e.workstreamId !== 'string') return false;
  if (typeof e.priority !== 'number') return false;
  if (typeof e.capacity !== 'object' || e.capacity === null) return false;
  const cap = e.capacity as Record<string, unknown>;
  if (typeof cap.Engineering !== 'number') return false;
  if (e.stories !== undefined) {
    if (!Array.isArray(e.stories)) return false;
    if (!e.stories.every(isValidStory)) return false;
  }
  return true;
};

const isValidCategory = (category: unknown): boolean => {
  if (typeof category !== 'object' || category === null) return false;
  const c = category as Record<string, unknown>;
  if (typeof c.id !== 'string') return false;
  if (typeof c.name !== 'string') return false;
  if (typeof c.color !== 'string') return false;
  return true;
};

export const validateEpicExportData = (data: unknown): data is EpicExportData => {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  if (typeof obj.version !== 'string') return false;
  if (!Array.isArray(obj.epics)) return false;
  if (!Array.isArray(obj.categories)) return false;
  if (!obj.epics.every(isValidEpic)) return false;
  if (!obj.categories.every(isValidCategory)) return false;
  return true;
};
